// One quiet row in a processing chain — a thinking block or a tool call.
// Low-contrast, content-width, borderless. No expand chevron: a failed tool
// tints its icon red, a running one shows a spinner, success stays silent.
// Open state is controlled by the parent chain (accordion); the body
// opens/closes with a grid-rows height transition.

import type { ReactNode } from 'react';
import { SparklesIcon, WrenchIcon } from '../ui/Icons';
import { Spinner } from '../ui/Spinner';

export type StepStatus = 'pending' | 'ok' | 'error' | 'none';

interface Props {
  kind: 'thinking' | 'tool';
  title: string;
  /** Dimmed one-line summary (a command, path, …). */
  target?: string;
  status: StepStatus;
  /** In-progress step — icon animates. */
  active?: boolean;
  body?: ReactNode;
  /** Controlled open state. */
  open: boolean;
  onToggle?: () => void;
}

export function StepRow({ kind, title, target, status, active, body, open, onToggle }: Props) {
  const hasBody = body != null;
  const isOpen = hasBody && open;
  const clickable = hasBody && onToggle != null;

  const Icon = kind === 'thinking' ? SparklesIcon : WrenchIcon;
  // The tool icon carries the result: red on failure, uncoloured otherwise.
  const iconColor =
    kind === 'thinking'
      ? 'text-thinking-accent'
      : status === 'error'
        ? 'text-error-text'
        : 'text-text-faint';
  return (
    <div>
      <button
        type="button"
        disabled={!clickable}
        onClick={() => onToggle?.()}
        className={`-mx-1.5 flex items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors ${
          clickable ? 'cursor-pointer hover:bg-elevated/60' : 'cursor-default'
        }`}
      >
        <span className={`flex h-4 w-4 shrink-0 items-center justify-center ${iconColor}`}>
          <Icon size={13} animate={kind === 'thinking' && active} />
        </span>
        {/* Open step reads one shade brighter — title and target each step up. */}
        <span
          className={`shrink-0 text-[12.5px] font-medium transition-colors ${
            isOpen ? 'text-text-primary' : 'text-text-secondary'
          }`}
        >
          {title}
        </span>
        {target ? (
          <span
            className={`max-w-[26rem] truncate font-mono text-[11.5px] transition-colors ${
              isOpen ? 'text-text-secondary' : 'text-text-faint'
            }`}
          >
            {target}
          </span>
        ) : null}
        {status === 'pending' ? <Spinner size={11} className="shrink-0 text-text-faint" /> : null}
      </button>
      {hasBody ? (
        <div
          className={`grid transition-all duration-200 ease-out ${
            isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="pb-1.5 pl-6 pr-1">{body}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
